import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Input, Space, Card, Tag, Row, Col, Select } from "antd";
import { Api_Url } from "../../../config/config";
import moment from "moment";
import axios from "axios";
import { BranchAssetsModalAdd } from "./BranchAssetsModalAdd";
import { BranchAssetsModalUpdate } from "./BranchAssetsModalUpdate";
import { ErrorToast, SuccessToast } from "../../ToastComponent"; // Added SuccessToast

const { Option } = Select;

export const BranchAssets = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);

  // Modal States
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // Filter States
  const [filters, setFilters] = useState({
    branch: "",
    searchTerm: "",
  });

  useEffect(() => {
    fetchBranches();
    fetchData();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/Branch?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      const branchNames = response?.data?.data?.map((b) => b.name) || [];
      setBranches([...new Set(branchNames)]);
    } catch (error) {
      console.error("Failed to load branches:", error);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${Api_Url}api/v1/BranchAssets?PageNumber=1&PageSize=1000`,
        {
          headers: {
            Authorization: `Bearer ${
              JSON.parse(sessionStorage.getItem("user"))?.accessToken
            }`,
          },
        }
      );
      setData(response?.data?.data || []);
    } catch (error) {
      ErrorToast("Failed to fetch branch assets");
    } finally {
      setLoading(false);
    }
  };

  // --- ADDED: EXCEL EXPORT HANDLER ---
  const handleExportReport = async () => {
    try {
      const response = await axios.get(`${Api_Url}api/v1/BranchAssets/export-branch-report`, {
        responseType: "blob", // Critical for downloading files
        headers: {
          Authorization: `Bearer ${
            JSON.parse(sessionStorage.getItem("user"))?.accessToken
          }`,
        },
      });

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Branch_Asset_Issue_Report_${moment().format("YYYYMMDD_HHmm")}.csv`);
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      SuccessToast("Report exported successfully!");
    } catch (error) {
      ErrorToast("Failed to export branch report");
      console.error("Export Error:", error);
    }
  };

  // Memoized client-side filtering
  const filteredData = useMemo(() => {
    let filtered = [...data];
    if (filters.branch) {
      filtered = filtered.filter(
        (item) =>
          item.branchName?.toLowerCase() === filters.branch.toLowerCase()
      );
    }
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      filtered = filtered.filter(
        (item) =>
          item.wifiName?.toLowerCase().includes(term) ||
          item.branchName?.toLowerCase().includes(term)
      );
    }
    return filtered;
  }, [data, filters]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ branch: "", searchTerm: "" });
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      sorter: (a, b) => a.id - b.id,
      width: 80,
    },
    {
      title: "Branch",
      dataIndex: "branchName",
      sorter: (a, b) => a.branchName.localeCompare(b.branchName),
    },
    {
      title: "Wifi Name",
      dataIndex: "wifiName",
    },
    {
      title: "Bill Due Date",
      dataIndex: "wifiBillDueDate",
      render: (date) => (date ? moment(date).format("DD-MM-YYYY") : "NA"),
    },
    {
      title: "Printers",
      dataIndex: "printerCount",
      render: (val) => val ?? 0,
    },
    {
      title: "CCTV",
      dataIndex: "cctvCount",
      render: (val) => val ?? 0,
    },
    {
      title: "MNTRA",
      dataIndex: "mntraCount",
      render: (val) => val ?? 0,
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Button
          type="primary"
          ghost
          onClick={() => {
            setSelectedAsset(record);
            setIsUpdateModalOpen(true);
          }}
        >
          Update Assets
        </Button>
      ),
    },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* Header Section */}
        <div className="row mb-3">
          <div className="col-12 d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Branch Assets Inventory</h3>
            <div className="d-flex gap-2"> {/* Added Flex container for multiple buttons */}
              {/* --- ADDED: EXPORT BUTTON --- */}
              <button
                className="btn btn-success"
                onClick={handleExportReport}
              >
                <i className="fa-solid fa-file-excel me-2"></i> Export Report
              </button>
              
              <button
                className="btn btn-primary"
                onClick={() => setIsAddModalOpen(true)}
              >
                <i className="fa-solid fa-plus me-2"></i> Add Branch Assets
              </button>
            </div>
          </div>
        </div>

        {/* Filter Card */}
        <Card className="mb-4">
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={8}>
              <Input
                placeholder="Search by Wifi Name..."
                value={filters.searchTerm}
                onChange={(e) =>
                  handleFilterChange("searchTerm", e.target.value)
                }
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                placeholder="Filter by Branch"
                value={filters.branch || undefined}
                onChange={(value) => handleFilterChange("branch", value)}
                allowClear
                showSearch
                style={{ width: "100%" }}
              >
                {branches.map((branch) => (
                  <Option key={branch} value={branch}>
                    {branch}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Button onClick={clearFilters} block>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card>

        {/* Table Section */}
        <div className="table-responsive">
          <Table
            className="table-striped"
            columns={columns}
            dataSource={filteredData}
            rowKey="id"
            loading={loading}
            pagination={{
              showSizeChanger: true,
              defaultPageSize: 10,
              pageSizeOptions: ["10", "20", "50"],
            }}
            scroll={{ x: 1000 }}
          />
        </div>

        {/* Modals */}
        {isAddModalOpen && (
          <BranchAssetsModalAdd
            onClose={() => setIsAddModalOpen(false)}
            onSuccess={() => {
              setIsAddModalOpen(false);
              fetchData();
            }}
          />
        )}

        {isUpdateModalOpen && (
          <BranchAssetsModalUpdate
            asset={selectedAsset}
            onClose={() => setIsUpdateModalOpen(false)}
            onSuccess={() => {
              setIsUpdateModalOpen(false);
              fetchData();
            }}
          />
        )}
      </div>
    </div>
  );
};